import type { ZoneFeatureKey } from '../services';

export type HubSectionKey = 'vpcs' | 'peering' | 'vpn' | 'public-ips' | 'legacy';

interface HubSection {
  key: HubSectionKey;
  label: string;
  /** Path segment under `/vpc` ('' = the index). */
  path: string;
  /** The section is hidden when the service's zone disables this feature. */
  feature: ZoneFeatureKey;
}

/** The sections of the VPC hub, in tab order. */
export const HUB_SECTIONS: HubSection[] = [
  { key: 'vpcs', label: 'VPCs', path: '', feature: 'vpc' },
  { key: 'peering', label: 'Peering', path: 'peering', feature: 'vpc' },
  { key: 'vpn', label: 'VPN', path: 'vpn', feature: 'elastic_ip' },
  { key: 'public-ips', label: 'Public IPs', path: 'public-ips', feature: 'elastic_ip' },
  { key: 'legacy', label: 'Legacy Networking', path: 'legacy', feature: 'networking' },
];

/** Detail routes under `/vpc`, declared outside the hub layout so they keep their own breadcrumbs. */
export const VPC_DETAIL_PATHS = { peeringGroup: 'peering/:groupId', vpc: ':vpcId' };

interface SubTab {
  key: string;
  label: string;
}

/** The `?tab=` sub-tabs of each section that has them (the VPCs section has none). */
export const SECTION_TABS: Record<Exclude<HubSectionKey, 'vpcs'>, SubTab[]> = {
  peering: [
    { key: 'groups', label: 'Peering groups' },
    { key: 'ranges', label: 'Peering ranges' },
  ],
  vpn: [
    { key: 'gateways', label: 'VPN gateways' },
    { key: 'connections', label: 'VPN connections' },
    { key: 'policies', label: 'Policies' },
  ],
  'public-ips': [
    { key: 'eip', label: 'Elastic IPs' },
    { key: 'snat', label: 'Source NAT' },
  ],
  legacy: [
    { key: 'private', label: 'Private networks' },
    { key: 'reserved', label: 'Reserved networks' },
    { key: 'routers', label: 'Virtual routers' },
  ],
};

/** The sections the zone offers, in their defined order. */
export function visibleSections(isEnabled: (key?: ZoneFeatureKey) => boolean): HubSection[] {
  return HUB_SECTIONS.filter((s) => isEnabled(s.feature));
}

type HubTarget = { kind: 'show' } | { kind: 'redirect'; to: string } | { kind: 'empty' };

/** What the hub does for a section: show it, send the caller to the first visible one, or show an empty state. */
export function resolveSection(key: HubSectionKey, isEnabled: (key?: ZoneFeatureKey) => boolean): HubTarget {
  const visible = visibleSections(isEnabled);
  if (visible.some((s) => s.key === key)) return { kind: 'show' };
  return visible.length ? { kind: 'redirect', to: visible[0].path } : { kind: 'empty' };
}

/** The section a path under `/vpc` belongs to ('' = the index), or null for a detail page. */
export function sectionAt(rel: string): HubSectionKey | null {
  const seg = rel.split('/').filter(Boolean);
  if (seg.length === 0) return 'vpcs';
  if (seg.length > 1) return null;
  return HUB_SECTIONS.find((s) => s.path === seg[0])?.key ?? null;
}

/** The requested `?tab=` key if the section has it, else the section's first tab. */
export function resolveTab(tabs: SubTab[], requested: string | null): string {
  return tabs.find((t) => t.key === requested)?.key ?? tabs[0].key;
}
